import React, { useState, useEffect } from 'react';
import { useDrop } from 'react-dnd';
import { FormattedMessage } from "react-intl";
import { CardsTable } from "./CardsTable";
import { CardsSelection } from "./CardsSelection";
import { DnDItemTypes, shuffleArray, stylePercentToFloat } from '../basic/Utilities';
import { DraggingCard } from "../components/DraggingCard";
import { getCardImgData } from "../components/DraggableCard";
import { Card } from "../components/Card";
import { Cards, CARDSPATH } from "../assets/gameData";

const initGroupedTableCards = (cardsSetup, tableWidth, cardConfig) => {
    const { max, grouped, rows, lastOpen, random, allOpen, sortedInGroup } = cardsSetup;
    const { cardHeight, cardSpace, cardRatio } = cardConfig;
    const tableHeight = window.innerHeight / 100 * (rows * cardHeight + (rows + 1) * cardSpace);
    const cardsSpacePx = window.innerHeight / 100 * cardSpace;
    const cardsHeight = window.innerHeight / 100 * cardHeight;
    const cardsWidth = cardsHeight / cardRatio;

    let cards = [];
    let currentPos = [0,0];
    let inGroup = 0;

    let cardIds = random !== false ? shuffleArray(Cards) : Cards.sort((a, b) =>  a.id - b.id);
    const cardSetsCount = Math.floor(max/cardIds.length);
    const groups = Math.ceil( max / grouped); // max was cardIds
    const cols = Math.ceil(groups / rows);

    const freeXSpace = tableWidth - cols * cardsWidth;
    const spacePx = [freeXSpace / (cols + 1), cardsSpacePx];
    const horizontalTableSpace = Math.max(0, 100 / tableWidth * ((spacePx[0] - spacePx[1]) * cols));
    const spaceProcent = [100 / tableWidth * spacePx[0], 100 / tableHeight * spacePx[1]];

    let cardIndex = 0;

    if(random && !sortedInGroup && cardSetsCount > 1){
        for (let i=1; i<cardSetsCount; i++){
            cardIds.push(...cardIds);
        }
    }

    const loopEnd = random && !sortedInGroup ? Math.min(cardIds.length, max) : max;

    for (let i = 0; i < loopEnd; i++) {
        let left = currentPos[0] * ((100 - horizontalTableSpace - spaceProcent[0]) / cols);
        let top = currentPos[1] * ((100 - spaceProcent[1]) / rows);

        left = !allOpen ? left - inGroup : left;
        top = !allOpen ? top + 2 * inGroup : top;

        cards.push({
            id: cardIds[random && !sortedInGroup ? i :cardIndex].id,
            left: parseFloat(left + horizontalTableSpace * 0.5 + spaceProcent[0] ) + '%',
            top: parseFloat(top + spaceProcent[1]) + '%',
            open: ((inGroup === grouped-1 && lastOpen) || allOpen) ? true : cardIds[random && !sortedInGroup ? i :cardIndex].open,
            place: 'cardsTable',
            index: i,
            key: i,
        });

        if(inGroup === grouped-1){
            inGroup = 0;

            cardIndex = (cardIndex >= cardIds.length) ? 0 : cardIndex+1;

            if(currentPos[0] < cols - 1) {
                currentPos[0]++;

            } else if(currentPos[0] === cols - 1) {
                currentPos[0] = 0;
                currentPos[1]++;
            }
        } else {
            inGroup++;
        }

    }

    return cards;
};

const initUngroupedTableCards = (cardsSetup, tableWidth, cardConfig) => {
    const { max, rows, lastOpen, random } = cardsSetup;
    const { cardHeight, cardSpace, cardRatio } = cardConfig;
    const tableHeight = window.innerHeight / 100 * (rows * cardHeight + (rows + 1) * cardSpace);
    const cardsSpacePx = window.innerHeight / 100 * cardSpace;
    const cardsHeight = window.innerHeight / 100 * cardHeight;
    const cardsWidth = cardsHeight / cardRatio;

    let cards = [];
    let currentPos = [0,0];

    const cardIds = random ? shuffleArray(Cards) : Cards;

    const cols = Math.ceil(max / rows);
    const freeXSpace = tableWidth - cols * cardsWidth;
    const spacePx = [freeXSpace / (cols + 1), cardsSpacePx];
    const horizontalTableSpace = Math.max(0, 100 / tableWidth * ((spacePx[0] - spacePx[1]) * cols));
    const spaceProcent = [100 / tableWidth * spacePx[0], 100 / tableHeight * spacePx[1]];

    for (let i = 0; i < Math.min(cardIds.length, max); i++) {
        const left = currentPos[0] * ((100 - horizontalTableSpace - spaceProcent[0]) / cols);
        const top = currentPos[1] * ((100 - spaceProcent[1]) / rows);

        cards.push({
            id: cardIds[i].id,
            left: parseFloat(left + horizontalTableSpace * 0.5 + spaceProcent[0] ) + '%',
            top: parseFloat(top + spaceProcent[1]) + '%',
            open: lastOpen,
            place: 'cardsTable',
            index: i,
            key: i,
        });

        if(currentPos[0] < cols - 1) {
            currentPos[0]++;
        } else {
            currentPos[0] = 0;
            currentPos[1]++;
        }
    }

    return cards;
};

export const Game = ({cardConfig, config, reset, onResetDone}) => {
    const {cardsSetup, allowedUserAction, selectionOptions} = config;
    const [cards, setCards] = useState([]);
    const [cardsTableVisible, toggleCardsTable] = useState(true);
    const [lastIndex, setLastIndex] = useState(0);
    const [zoomCard, toggleZoomCard] = useState(undefined);
    const [activeCard, setActiveCard] = useState(undefined);
    const internCardsTable = React.useRef(null);
    const internCardsSelection = React.useRef(null);

    const toggleTableBtnClasses = "btn btn-info " + (cardsTableVisible ? "icon-hide-primary" : "icon-show-primary");

    const setZoomCard = (id) => {
        toggleZoomCard(id ? getCardImgData(id) : undefined);
    };

    const changeActiveCard = (id) => {
        setActiveCard(id !== activeCard ? id : undefined);
    };

    const [, drop] = useDrop({
        accept: DnDItemTypes.CARD,
        /*drop(item) {
            console.log('dropped', item);
            console.log(cards);
        }*/
    });

    useEffect(() => {
        setCards(
            cardsSetup.grouped && cardsSetup.grouped > 1 ?
                initGroupedTableCards(cardsSetup, internCardsTable.current.clientWidth, cardConfig) :
                initUngroupedTableCards(cardsSetup, internCardsTable.current.clientWidth, cardConfig)
        );
        setActiveCard(undefined);
        toggleZoomCard(undefined);
        setLastIndex(cardsSetup.max);
        if(reset) onResetDone();
    }, [cardsSetup, internCardsTable, cardConfig, reset, onResetDone]);

    const moveCard = (id, left, top, open, ref, itemKey) => {
        let updatedCards = Object.assign([], cards);

        const topFloat = stylePercentToFloat(top);

        const verticalSpaceBetweenAreas = 56;// @todo: calc new top when multiplayer, Problem no referenz from start area, only destination

        const spaceProcent = (ref === "cardsTable") ? 100 / internCardsTable.current.clientHeight * verticalSpaceBetweenAreas : 100 / internCardsSelection.current.clientHeight * verticalSpaceBetweenAreas;
        const topInNewArea = topFloat > 0 ? topFloat - (100 + spaceProcent) : topFloat + (100 + spaceProcent);

        const leftInNewArea = 50;

        updatedCards[itemKey].top = ref === cards[itemKey].place ? top : topInNewArea + '%';
        updatedCards[itemKey].left = ref === "cardsTable" && (cards[itemKey].place !== "cardsTable" && cards[itemKey].place !== "cardsSelection") ? leftInNewArea + '%' : left; // areas with same width and left position!

        updatedCards[itemKey].open = ref !== cards[itemKey].place ? open : cards[itemKey].open;
        updatedCards[itemKey].place = ref;

        setLastIndex(lastIndex + 1);
        updatedCards[itemKey].index = lastIndex;

        setCards(updatedCards);
    };

    const cardSelected = (selectionSpaceNameId, cardId, itemKey) => {
        //single cards space
        let updatedCards = Object.assign([], cards);
        updatedCards[itemKey].top = '0%';
        updatedCards[itemKey].left = '0%';
        updatedCards[itemKey].place = selectionSpaceNameId;
        setLastIndex(lastIndex + 1);
        updatedCards[itemKey].index = lastIndex;
        setCards(updatedCards);
    };

    const toggleCardSide = (itemKey) => {
        let updatedCards = Object.assign([], cards);
        updatedCards[itemKey].open = !updatedCards[itemKey].open;
        setCards(updatedCards);
    };

    const getSelectionHeight = () => {
        if(selectionOptions.defaultSpaces)
            return parseFloat(cardConfig.cardHeight + 2 * cardConfig.cardSpace + 9) + 'vh';
        else if (selectionOptions.fixedHeight)
            return parseFloat(selectionOptions.fixedHeight * cardConfig.cardHeight + (selectionOptions.fixedHeight + 1) * cardConfig.cardSpace) + 'vh';
        else
            return parseFloat(2 * cardConfig.cardHeight + 3 * cardConfig.cardSpace) + "vh";
    };

    return (
        <div className="gameArea" ref={drop}>
            <div ref={internCardsTable}>
                <CardsTable
                    id = "cardsTable"
                    height={parseFloat(cardsSetup.rows * cardConfig.cardHeight + (cardsSetup.rows + 1) * cardConfig.cardSpace) + 'vh'}
                    className = {cardsTableVisible ? "" : " d-none"}
                    allowedActions = {allowedUserAction}
                    cards = {cards.filter((item) => item.place === "cardsTable")}
                    onCardMove = {moveCard}
                    onCardZoom = {setZoomCard}
                    activeCard = {activeCard}
                    onToggleActiveCard = {changeActiveCard}
                    onToggleCardSide={toggleCardSide}
                />
            </div>
            <div className="cardsTable-footer">
                <button className={toggleTableBtnClasses} onClick={()=>{toggleCardsTable( !cardsTableVisible )}}>
                    <FormattedMessage id="cardsTable.name" values={{state: <FormattedMessage id={cardsTableVisible ? "area.state.hide" : "area.state.show"} />}} />
                </button>
            </div>
            <div ref={internCardsSelection}>
                <CardsSelection
                        id = "cardsSelection"
                        className = {!selectionOptions.defaultSpaces ? "flex-space" : ""}
                        height={getSelectionHeight()}
                        allowedActions = {allowedUserAction}
                        config = {selectionOptions}
                        cards = {cards}
                        onCardMove = {moveCard}
                        onCardSelected = {cardSelected}
                        onCardZoom = {setZoomCard}
                        activeCard = {activeCard}
                        onToggleActiveCard = {changeActiveCard}
                        onToggleCardSide={toggleCardSide}
                        reset={reset}
                />
            </div>
            <DraggingCard />

            {zoomCard && (
                <div className="modal show"
                     id="cardZoomView"
                     data-keyboard="false"
                     tabIndex="-1"
                     role="dialog"
                     aria-hidden="false"
                     style={{display:"block"}}
                >
                    <div className="modal-dialog">
                        <div className="modal-content">
                            <div className="modal-header">
                                <h5 className="modal-title" id="staticBackdropLabel"><FormattedMessage id="modal.zoom.title" values={{cardId: zoomCard.id}} /></h5>
                                <button type="button" className="close" data-dismiss="modal" aria-label="Close" onClick={()=>setZoomCard(undefined)}>
                                    <span aria-hidden="true">&times;</span>
                                </button>
                            </div>
                            <div className="modal-body">
                                <div className="gm-card zoom">
                                    <Card src={CARDSPATH + zoomCard.img}/>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};
